import React, { useState } from 'react';
import { useQuery } from 'react-apollo';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import {
  CardElement,
  useElements,
  useStripe,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';

import FormGroup from '../../../../../../components/global/FormGroup';
import Alert from '../../../../../../components/global/Alert';
import Button from '../../../../../../components/global/Button';
import { getErrorString } from '../../../../../../util/normalize-errors';
import { sentryCaptureMessage } from '../../../../../../util/sentry';
import { getPhotoSearchCheckoutSearchItemsQuery } from '../../../../../temp/views/onboarding/PhotoSearchCheckout/graphql/queries/getPhotoSearchCheckoutSearchItems';
import RegistrationPaymentDurationSelect from './RegistrationPaymentDurationSelect';
import usePaymentRequestButton from '../../../../../../components/global/StripeCheckout/usePaymentRequestButton';

const propTypes = {
  className: PropTypes.string,
  values: PropTypes.shape({
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    plan_id: PropTypes.string,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  onCheckout: PropTypes.func.isRequired,
};

const CARD_ELEMENT_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: '#969ea0',
      color: '#3c4c51',
      fontWeight: 800,
      fontFamily: 'Muli, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',

      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: '#969ea0',
        fontWeight: 800,
      },
    },
    invalid: {
      iconColor: '#ea3770',
      color: '#ea3770',
    },
  },
};

const RegistrationPayment = ({
  className,
  values,
  submitting,
  onCheckout,
  handleChange,
}) => {
  const classes = classNames({
    [className]: !!className,
    'view-register-checkout-form': true,
  });
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');
  const [name, setName] = useState(
    `${values.first_name} ${values.last_name}`.trim(),
  );
  const [email, setEmail] = useState('');
  const { data: { searchSubscriptionItems = [] } = {} } = useQuery(
    getPhotoSearchCheckoutSearchItemsQuery,
  );
  const searchSubscriptionItemPlans = searchSubscriptionItems.filter(
    searchSubscriptionItem => searchSubscriptionItem.plan === values.plan,
  );
  const selectedPlan = searchSubscriptionItems.find(
    searchSubscriptionItem => searchSubscriptionItem.id === values.plan_id,
  );
  const { paymentRequest } = usePaymentRequestButton({
    amount: selectedPlan?.price?.value,
    onStripeToken: async ({ token }) => {
      await onCheckout({
        ...values,
        stripe_token: token,
      });
    },
  });

  const handleSubmit = async event => {
    event.preventDefault();

    if (loading || submitting) {
      return;
    }

    setLoading(true);
    setStripeError('');
    try {
      const { error, token } = await stripe.createToken(
        elements.getElement(CardElement),
        {
          name,
          email,
        },
      );

      if (error) {
        throw Error(error.message);
      }

      onCheckout({
        ...values,
        stripe_token: token,
      });
    } catch (error) {
      setStripeError(getErrorString(error));
      sentryCaptureMessage('SHUTTERBUG SIGNUP - CC Capture Error ', {
        level: 'error',
        extra: {
          error,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className={classes} onSubmit={handleSubmit}>
      {searchSubscriptionItemPlans && (
        <RegistrationPaymentDurationSelect
          className="mb-5"
          searchSubscriptionItems={searchSubscriptionItemPlans}
          selectedSearchSubscriptionItemId={values.plan_id}
          onSearchSubscriptionItemSelect={id => {
            handleChange({
              target: {
                name: 'plan_id',
                value: id,
              },
            });
          }}
        />
      )}

      <h3 className="text-center mb-2">Your Payment Details</h3>
      <fieldset className="view-register-checkout-fieldset">
        <FormGroup
          label="Name"
          labelSrOnly
          id="card-name"
          name="name"
          placeholder="Name on card"
          required
          className="mb-1"
          value={name}
          onChange={event => {
            const { target } = event;
            const { value } = target;
            setName(value);
          }}
          disabled={submitting || loading}
        />

        <FormGroup
          label="Email"
          labelSrOnly
          id="card-email"
          type="email"
          name="email"
          placeholder="Your email"
          required
          value={email}
          onChange={event => {
            const { target } = event;
            const { value } = target;
            setEmail(value);
          }}
          disabled={submitting || loading}
        />
      </fieldset>

      <fieldset className="view-register-checkout-fieldset view-register-checkout-fieldset-credit-card">
        <CardElement
          options={CARD_ELEMENT_OPTIONS}
          disabled={submitting || loading}
        />
      </fieldset>

      <fieldset className="view-register-checkout-fieldset">
        {paymentRequest && (
          <PaymentRequestButtonElement options={{ paymentRequest }} />
        )}
      </fieldset>

      <p className="text-center mb-4">
        Cancel anytime. Your card will not be charged until{' '}
        {moment().add(30, 'days').format('MMM Do')}.
      </p>

      {stripeError && (
        <Alert type="danger" className="mt-3">
          {stripeError}
        </Alert>
      )}

      <div className="text-center mt-3">
        <Button
          id="shutterbug-reg-btn-form-submit"
          type="submit"
          className="view-register-cta"
          disabled={submitting || loading}
        >
          {submitting || loading ? 'Processing...' : 'Start My Free Trial Now'}
        </Button>
      </div>
    </form>
  );
};

RegistrationPayment.propTypes = propTypes;

export default RegistrationPayment;
